import * as React from "react";
import {PropsWithChildren, useRef} from "react";
import {inject, observer} from "mobx-react";

import clipsFullscreenClasses from "../../ClipsFullscreen.styl";

import {ClipsFullscreenStore} from "../../../../lib/ClipsFullscreenStore";
import {
    Clip as TClip,
    DivExtendedByInViewportFunction,
    FullscreenItemType
} from "../../../../lib/types";
import {INTERSECTION_OBSERVER_THRESHOLDS} from "../../../../config";



interface ICommonFullscreenItemWrapper extends PropsWithChildren {
    store?: ClipsFullscreenStore,
    bean?: TClip;
    itemType: FullscreenItemType
}
export const CommonFullscreenItemWrapper: React.FC<ICommonFullscreenItemWrapper> = inject("store")(observer(
    (props) => {
        const store = props.store!;

        const rootRef = React.useRef<DivExtendedByInViewportFunction>(null);
        const [isEnterViewport, setIsEnterViewport] = React.useState(false);

        const observerRef = React.useRef<IntersectionObserver | null>(null);
        const timeout = useRef<ReturnType<typeof setTimeout>>();

        React.useEffect(() => {
            const ref = rootRef.current;

            if (store.isViaUnionObserverFeature) {
                /* Т.к обсервер инициализируется по дереву компонентов выше, то пробуем через таймаут - акутально для
                 *  входа в фуллскрин и первого клипа
                 * */
                if (!store.unionObserver) {
                    timeout.current = setTimeout(() => {
                        bindObserver(store.unionObserver, ref, setIsEnterViewport);
                    }, 0);
                } else {
                    bindObserver(store.unionObserver, ref, setIsEnterViewport);
                }
            } else {
                const callback = (entries: IntersectionObserverEntry[]) => {
                    const [entry] = entries;
                    setIsEnterViewport(entry.isIntersecting);
                };

                const observer = new IntersectionObserver(callback, {
                    threshold: INTERSECTION_OBSERVER_THRESHOLDS.COMMON_ITEM
                });

                if (ref) {
                    observerRef.current = observer;
                    observer.observe(ref);
                }
            }


            return () => {
                if (store.isViaUnionObserverFeature) {
                    if (store.unionObserver && ref) {
                        store.unionObserver.unobserve(ref);
                    }

                    if (timeout.current) {
                        clearTimeout(timeout.current);
                    }

                } else {
                    const observer = observerRef.current;

                    if (observer) {
                        observer.disconnect();
                    }
                }
            };
        }, [store.isViaUnionObserverFeature, store.unionObserver]);


        React.useEffect(() => {
            if (isEnterViewport) {
                if (props.itemType === FullscreenItemType.CLIP && props.bean) {
                    store.setCurrentIndexByMovieId(props.bean.meta.movie.movieId);
                    store.checkOffset();
                    store.setIsAnimationRunning(false);
                }
            }
        }, [isEnterViewport, props.itemType, store, props.bean]);

        return (
            <div ref={rootRef} className={clipsFullscreenClasses["height100"]}>
                {props.children}
            </div>
        );
    })
);

function bindObserver(
    observer: IntersectionObserver | null,
    ref: DivExtendedByInViewportFunction | null,
    setIsEnterViewport: React.Dispatch<React.SetStateAction<boolean>>
) {
    if (observer && ref) {
        ref.setInViewport = setIsEnterViewport;
        observer.observe(ref);
    }
}
